import { useTranslation, useTranslate } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Modal } from 'module/ui/components/atoms/modal/modal';
import { PopoverSelect } from 'module/ui/components/molecules/popover-select';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Document } from 'api/pages';
import { useUpdateCompanyPageSources } from 'queries/document-agent';
import {
  useCompanyV2SourcesQuery,
  CompanyWiseSourceEmail,
} from 'queries/meeting';
import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import type { Option } from 'module/ui/components/atoms/select';

const StyledModal = styled(Modal)`
  .ant-modal-body {
    overflow: visible;
  }
  .ant-modal-content {
    overflow: visible;
  }
`;

const ModalFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  min-width: 500px;
  overflow: visible;
`;

const ModalLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: var(--line-height-01);
  color: var(--text-primary);
`;

const ModalSelectWrapper = styled.div`
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  width: 90%;
`;

const ModalTriggerContent = styled.div`
  color: var(--text-secondary);
`;

const ModalFooterWrapper = styled.div`
  display: flex;
  gap: var(--spacing-04);
  justify-content: flex-end;

  button {
    min-width: 120px;
    justify-content: center;
  }
`;

interface AddSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  document?: Document;
  documentId?: number;
  companyId: number;
  onSuccess?: () => void;
  showMeetingDropdown?: boolean;
  showEmailDropdown?: boolean;
}

export const AddSourceModal = ({
  isOpen,
  onClose,
  document: _document,
  documentId,
  companyId,
  onSuccess,
  showMeetingDropdown = false,
  showEmailDropdown = false,
}: AddSourceModalProps) => {
  const translate = useTranslate();
  const modalTitle = useTranslation('add_source_modal.title');
  const addingLabel = useTranslation('add_source_modal.adding');
  const cancelLabel = useTranslation('cancel');
  const addLabel = useTranslation('add');
  const sourceMeetingsLabel = useTranslation(
    'add_source_modal.source_meetings',
  );
  const sourceEmailsLabel = useTranslation('add_source_modal.source_emails');
  const selectSourceMeetingsLabel = useTranslation(
    'add_source_modal.select_source_meetings',
  );
  const selectSourceEmailsLabel = useTranslation(
    'add_source_modal.select_source_emails',
  );
  const noMeetingsSelectedLabel = useTranslation(
    'add_source_modal.no_meetings_selected',
  );
  const noEmailsSelectedLabel = useTranslation(
    'add_source_modal.no_emails_selected',
  );

  const queryClient = useQueryClient();
  const {
    mutateAsync: updateCompanyPageSources,
    isPending: isUpdatingSources,
  } = useUpdateCompanyPageSources();

  const [addSourceModalState, setAddSourceModalState] = useState({
    selectedMeetingOptions: [] as Option[],
    selectedEmailOptions: [] as Option[],
    meetingSearchTerm: '',
    emailSearchTerm: '',
    meetingOffset: 0,
    emailOffset: 0,
  });

  // Accumulated options for pagination
  const [accumulatedMeetingOptions, setAccumulatedMeetingOptions] = useState<
    Option[]
  >([]);
  const [accumulatedEmailOptions, setAccumulatedEmailOptions] = useState<
    Option[]
  >([]);

  const meetingLimit = 20;
  const emailLimit = 20;

  const [hasMoreMeetings, setHasMoreMeetings] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(false);

  // Fetch available meetings for modal
  const { data: meetingsData, isLoading: isMeetingsLoading } =
    useCompanyV2SourcesQuery(
      {
        documentId: documentId!,
        sourceType: 'MEETING',
        companyId,
        limit: meetingLimit,
        offset: addSourceModalState.meetingOffset,
        isEnabled: true,
        fetchUsed: false,
      },
      {
        enabled: isOpen && !!documentId,
      },
    );

  // Fetch available emails for modal
  const { data: emailsData, isLoading: isEmailsLoading } =
    useCompanyV2SourcesQuery<CompanyWiseSourceEmail>(
      {
        documentId: documentId!,
        sourceType: 'EMAIL',
        companyId,
        limit: emailLimit,
        offset: addSourceModalState.emailOffset,
        isEnabled: true,
        fetchUsed: false,
      },
      {
        enabled: isOpen && !!documentId,
      },
    );

  // Update hasMore flags
  useEffect(() => {
    if (meetingsData) {
      setHasMoreMeetings(meetingsData.hasMore);
    }
  }, [meetingsData]);

  useEffect(() => {
    if (emailsData) {
      setHasMoreEmails(emailsData.hasMore);
    }
  }, [emailsData]);

  // Accumulate meeting options when new data arrives
  useEffect(() => {
    if (meetingsData?.data && isOpen) {
      const newOptions = meetingsData.data.map((meeting) => ({
        label: meeting.meetingName,
        value: meeting.meetingId,
      }));

      if (addSourceModalState.meetingOffset === 0) {
        // Reset on first load or search reset
        setAccumulatedMeetingOptions(newOptions);
      } else {
        // Append new options for pagination
        setAccumulatedMeetingOptions((prev) => {
          const existingValues = prev.map((opt) => opt.value);
          const uniqueNewOptions = newOptions.filter(
            (opt) => !existingValues.includes(opt.value),
          );

          return [...prev, ...uniqueNewOptions];
        });
      }
    }
  }, [meetingsData, addSourceModalState.meetingOffset, isOpen]);

  // Accumulate email options when new data arrives
  useEffect(() => {
    if (emailsData?.data && isOpen) {
      const newOptions = emailsData.data.map((email) => ({
        label: email.subject || 'No Subject',
        value: email.emailThreadId,
      }));

      if (addSourceModalState.emailOffset === 0) {
        // Reset on first load or search reset
        setAccumulatedEmailOptions(newOptions);
      } else {
        // Append new options for pagination
        setAccumulatedEmailOptions((prev) => {
          const existingValues = prev.map((opt) => opt.value);
          const uniqueNewOptions = newOptions.filter(
            (opt) => !existingValues.includes(opt.value),
          );

          return [...prev, ...uniqueNewOptions];
        });
      }
    }
  }, [emailsData, addSourceModalState.emailOffset, isOpen]);

  const meetingOptions = accumulatedMeetingOptions;
  const emailOptions = accumulatedEmailOptions;

  // Handlers for loading more data
  const handleLoadMoreMeetings = useCallback(() => {
    setAddSourceModalState((prev) => ({
      ...prev,
      meetingOffset: prev.meetingOffset + meetingLimit,
    }));
  }, [meetingLimit]);

  const handleLoadMoreEmails = useCallback(() => {
    setAddSourceModalState((prev) => ({
      ...prev,
      emailOffset: prev.emailOffset + emailLimit,
    }));
  }, [emailLimit]);

  // Use stored Option[] as source of truth so selection is visible before Done and survives option-list changes (e.g. search)
  const selectedMeetingValues = addSourceModalState.selectedMeetingOptions;
  const selectedEmailValues = addSourceModalState.selectedEmailOptions;

  const handleMeetingsChange = (value: Option[]) => {
    setAddSourceModalState((prev) => ({
      ...prev,
      selectedMeetingOptions: value,
    }));
  };

  const handleEmailsChange = (value: Option[]) => {
    setAddSourceModalState((prev) => ({
      ...prev,
      selectedEmailOptions: value,
    }));
  };

  const resetAddSourceModal = useCallback(() => {
    setAddSourceModalState({
      selectedMeetingOptions: [],
      selectedEmailOptions: [],
      meetingSearchTerm: '',
      emailSearchTerm: '',
      meetingOffset: 0,
      emailOffset: 0,
    });
    onClose?.();
  }, [onClose]);

  const handleAdd = useCallback(async () => {
    if (!documentId) {
      return;
    }

    try {
      const selectedMeetingIds = addSourceModalState.selectedMeetingOptions.map(
        (o) => o.value as number,
      );
      const selectedEmailIds = addSourceModalState.selectedEmailOptions.map(
        (o) => o.value as number,
      );

      await updateCompanyPageSources({
        documentId,
        payload: {
          EMAIL: selectedEmailIds.length > 0 ? selectedEmailIds : undefined,
          MEETING:
            selectedMeetingIds.length > 0 ? selectedMeetingIds : undefined,
        },
      });

      // Refetch the document after successful update
      await queryClient.invalidateQueries({
        queryKey: ['document', documentId],
      });

      onSuccess?.();
      resetAddSourceModal();
      onClose?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update sources:', error);
    }
  }, [
    documentId,
    updateCompanyPageSources,
    addSourceModalState.selectedMeetingOptions,
    addSourceModalState.selectedEmailOptions,
    queryClient,
    onSuccess,
    resetAddSourceModal,
    onClose,
  ]);

  return (
    <StyledModal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FontAwesomeIcon icon={faGlobe} size={12} />
          {modalTitle}
        </div>
      }
      open={isOpen}
      onCancel={resetAddSourceModal}
      footer={
        <ModalFooterWrapper>
          <Button variant="secondary" onClick={resetAddSourceModal}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={isUpdatingSources}
          >
            {isUpdatingSources ? addingLabel : addLabel}
          </Button>
        </ModalFooterWrapper>
      }
    >
      <ModalFormSection>
        {showMeetingDropdown && (
          <div>
            <ModalLabel>{sourceMeetingsLabel}</ModalLabel>
            <ModalSelectWrapper>
              <PopoverSelect
                title={selectSourceMeetingsLabel}
                triggerContent={
                  <ModalTriggerContent>
                    {addSourceModalState.selectedMeetingOptions.length === 0
                      ? noMeetingsSelectedLabel
                      : translate('add_source_modal.meeting_selected', {
                          count:
                            addSourceModalState.selectedMeetingOptions.length,
                        })}
                  </ModalTriggerContent>
                }
                isMulti
                options={meetingOptions}
                value={selectedMeetingValues}
                onChange={handleMeetingsChange}
                isLoading={isMeetingsLoading}
                onLoadMore={handleLoadMoreMeetings}
                hasMore={hasMoreMeetings}
                onInputChange={(value, action) => {
                  if (action.action === 'input-change') {
                    setAddSourceModalState((prev) => ({
                      ...prev,
                      meetingSearchTerm: value,
                      meetingOffset: 0,
                    }));
                  }
                }}
                checkboxColor="black"
              />
            </ModalSelectWrapper>
          </div>
        )}
        {showEmailDropdown && (
          <div>
            <ModalLabel>{sourceEmailsLabel}</ModalLabel>
            <ModalSelectWrapper>
              <PopoverSelect
                title={selectSourceEmailsLabel}
                triggerContent={
                  <ModalTriggerContent>
                    {addSourceModalState.selectedEmailOptions.length === 0
                      ? noEmailsSelectedLabel
                      : translate('add_source_modal.email_selected', {
                          count:
                            addSourceModalState.selectedEmailOptions.length,
                        })}
                  </ModalTriggerContent>
                }
                isMulti
                options={emailOptions}
                value={selectedEmailValues}
                onChange={handleEmailsChange}
                isLoading={isEmailsLoading}
                onLoadMore={handleLoadMoreEmails}
                hasMore={hasMoreEmails}
                onInputChange={(value, action) => {
                  if (action.action === 'input-change') {
                    setAddSourceModalState((prev) => ({
                      ...prev,
                      emailSearchTerm: value,
                      emailOffset: 0,
                    }));
                  }
                }}
                checkboxColor="black"
              />
            </ModalSelectWrapper>
          </div>
        )}
      </ModalFormSection>
    </StyledModal>
  );
};
